<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <v-container fluid>
    <v-container grid-list-xl>
      <v-layout row wrap align-center>
        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.projects.departmentLabel') }}</label>
          <v-overflow-btn
            :value="department"
            :items="getDepartments(true)"
            item-text="Number"
            :return-object="true"
            editable
            clearable
            hide-details
            :label="$isLoading('departments') ? 'Loading...' : 'Choose Department'"
            :loading="$isLoading('departments')"
            @input="setDepartmentDebounce"
            @click:clear="setDepartmentDebounce(getDefaultDepartment())"
          ></v-overflow-btn>
        </v-flex>

        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.projects.categoryLabel') }}</label>
          <v-overflow-btn
            :value="category"
            :items="getCategories(true)"
            item-text="Name"
            :return-object="true"
            editable
            clearable
            hide-details
            :label="$isLoading('categories') ? 'Loading...' : 'Choose Category'"
            :loading="$isLoading('categories')"
            @input="setCategoryDebounce"
            @click:clear="setCategoryDebounce(getDefaultCategory())"
          ></v-overflow-btn>
        </v-flex>

        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.projects.statusLabel') }}</label>
          <v-overflow-btn
            :value="status"
            :items="getStatuses(true)"
            item-text="Name"
            :return-object="true"
            editable
            clearable
            hide-details
            :label="$isLoading('statuses') ? 'Loading...' : 'Choose Status'"
            :loading="$isLoading('statuses')"
            @input="setStatusDebounce"
            @click:clear="setStatusDebounce(getDefaultStatus())"
          ></v-overflow-btn>
        </v-flex>

        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.projects.searchLabel') }}</label>
          <v-text-field
            class="search-text-field"
            @input="setSearchDebounce"
            append-icon="search"
            :label="$language('pages.projects.searchPlaceholder')"
            single-line
            clearable
            :height="49"
            hide-details
            @click:clear="setSearchDebounce('')"
            :loading="$isLoading('search')"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>

    <v-progress-linear v-if="$isLoading('created')" :height="2" :indeterminate="true"></v-progress-linear>

    <v-data-table
      v-else
      :headers="headers"
      :items="items"
      class="elevation-1 text-left w-100"
      :pagination.sync="pagination"
      :no-results-text="$language('table.noResults')"
      :no-data-text="$language('table.noRecords')"
      :rows-per-page-text="$language('table.pagination.rowsPerPage')"
      :rows-per-page-items="rows"
      :custom-filter="searchFilter"
      :search="search"
      :loading="$isLoading('departments')
        || $isLoading('categories')
        || $isLoading('statuses')
        || $isLoading('search')"
    >
      <template slot="progress">
        <div class="v-progress-linear">
          <div class="v-progress-linear__bar">
            <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
              <div class="v-progress-linear__bar__indeterminate long primary"></div>
              <div class="v-progress-linear__bar__indeterminate short primary"></div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:items="{ index: rowIndex, item: project }">
        <tr>
          <td class="pa-0">
            <input
              class="input-field"
              :class="{ 'input--error': project.Name === '' }"
              type="text"
              :value="project.Name"
              @keyup.enter="update($event.target, project, 'Name', $event.target.value)"
              @blur="update($event.target, project, 'Name', $event.target.value)"
            />
            <div v-if="$isLoading('project-' + project.ID + '-Name')" class="v-progress-linear">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
          </td>

          <td class="pa-0">
            <input
              class="input-field"
              type="text"
              :ref="'project-responsible-' + project.ID"
              :value="dropdownGetValue($refs['project-responsible-' + project.ID], project.Responsible)"
              @input="dropdownSetFilter($event.target.value)"
              @click="dropdownShow($event.target, projects.responsible)"
              @keyup.down="dropdownShow($event.target, projects.responsible)"
              @blur="updateDebounce($event.target, project, 'Responsible', $event.target.value)"
              @dropdown-select="updateDebounce($event.target, project, 'Responsible', $event.selectedValue)"
            />
            <div v-if="$isLoading('project-' + project.ID + '-Responsible')" class="v-progress-linear">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
          </td>

          <td class="pa-0">
            <input
              class="input-field"
              type="text"
              :ref="'project-department-' + project.ID"
              :value="dropdownGetValue($refs['project-department-' + project.ID], getDepartmentBy('ID', project.DepartmentID).Number)"
              @blur="$event.target.value = getDepartmentBy('ID', project.DepartmentID).Number"
              @input="dropdownSetFilter($event.target.value)"
              @click="dropdownShow($event.target, getDepartments(), { itemText: 'Number', itemValue: 'ID' })"
              @keyup.down="dropdownShow($event.target, getDepartments(), { itemText: 'Number', itemValue: 'ID' })"
              @dropdown-select="update($event.target, project, 'DepartmentID', $event.selectedValue)"
            />
            <div v-if="$isLoading('project-' + project.ID + '-DepartmentID')" class="v-progress-linear">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
          </td>

          <td class="pa-0">
            <input
              class="input-field"
              type="text"
              :ref="'project-category-' + project.ID"
              :value="dropdownGetValue($refs['project-category-' + project.ID], getCategoryBy('ID', project.ProjectCategoryID).Name)"
              @blur="$event.target.value = getCategoryBy('ID', project.ProjectCategoryID).Name"
              @input="dropdownSetFilter($event.target.value)"
              @click="dropdownShow($event.target, getCategories(), { itemText: 'Name', itemValue: 'ID' })"
              @keyup.down="dropdownShow($event.target, getCategories(), { itemText: 'Name', itemValue: 'ID' })"
              @dropdown-select="update($event.target, project, 'ProjectCategoryID', $event.selectedValue)"
            />
            <div v-if="$isLoading('project-' + project.ID + '-ProjectCategoryID')" class="v-progress-linear">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
          </td>

          <td class="pa-0">
            <input
              class="input-field"
              :class="{ 'input--error': project.OrderValue === '' }"
              type="text"
              :value="$helpers.formatNumber(project.OrderValue)"
              @keyup="$event.target.value = $helpers.formatNumber($event.target.value)"
              @keyup.enter="update($event.target, project, 'OrderValue', $event.target.value ? parseInt($event.target.value.replace(/ /g, ''), 10) : '')"
              @blur="update($event.target, project, 'OrderValue', $event.target.value ? parseInt($event.target.value.replace(/ /g, ''), 10) : '')"
            />
            <div v-if="$isLoading('project-' + project.ID + '-OrderValue')" class="v-progress-linear">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
          </td>

          <td class="pa-0">
            <input
              class="input-field"
              type="text"
              style="width: 300px;"
              :ref="'project-departments-' + project.ID"
              :value="dropdownGetValue($refs['project-departments-' + project.ID], project.DepartmentIDs.map(DepartmentID => getDepartmentBy('ID', DepartmentID).Number).sort().join(', '))"
              @input="dropdownSetFilter($event.target.value)"
              @click="dropdownShow($event.target, getDepartments(), { itemText: 'Number', itemValue: 'ID', multiple: true, selected: project.DepartmentIDs })"
              @keyup.down="dropdownShow($event.target, getDepartments(), { itemText: 'Number', itemValue: 'ID', multiple: true, selected: project.DepartmentIDs })"
              @dropdown-hide="updateDepartmentIDs($event.target, project, $event.selectedValues)"
            />
            <div v-if="$isLoading('project-' + project.ID + '-DepartmentIDs')" class="v-progress-linear">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
          </td>

          <td class="pa-0">
            <input
              class="input-field"
              type="text"
              :ref="'project-status-' + project.ID"
              :value="dropdownGetValue($refs['project-status-' + project.ID], getStatusBy('ID', project.ProjectStatusID).Name)"
              @blur="$event.target.value = getStatusBy('ID', project.ProjectStatusID).Name"
              @input="dropdownSetFilter($event.target.value)"
              @click="dropdownShow($event.target, getStatuses(), { itemText: 'Name', itemValue: 'ID' })"
              @keyup.down="dropdownShow($event.target, getStatuses(), { itemText: 'Name', itemValue: 'ID' })"
              @dropdown-select="update($event.target, project, 'ProjectStatusID', $event.selectedValue)"
            />
            <div v-if="$isLoading('project-' + project.ID + '-ProjectStatusID')" class="v-progress-linear">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
          </td>

          <td class="text-center">
            <!-- v-if="!project.Planning" -->
            <i
              tabindex="0"
              class="v-icon delete-icon material-icons theme--light"
              style="cursor: pointer; color: gray;"
              @click="showRemoveProjectModal(project)"
              @keyup.13="showRemoveProjectModal(project)"
            >delete</i>

            <!-- <i
              v-else
              @mouseover="showHoverRemoveButtonTooltip($event.target)"
              @mouseout="hideHoverRemoveButtonTooltip()"
              aria-hidden="true"
              tabindex="0"
              class="v-icon delete-icon material-icons theme--light"
              style="cursor: pointer; color: lightgray;"
            >delete</i> -->
          </td>
        </tr>
      </template>

      <template slot="footer">
        <td>
          <div>
            <strong>{{ $language('pages.projects.summary') }}</strong>
          </div>
        </td>
        <td></td>
        <td></td>
        <td></td>
        <td>
          <strong>
            {{ $helpers.formatNumber(items
            .map(({ OrderValue }) => parseInt(OrderValue.toString().replace(/ /g, ''), 10))
            .reduce((summary, orderValue) => summary + orderValue, 0)) }}
          </strong>
        </td>
        <td></td>
        <td></td>
        <td></td>
      </template>
    </v-data-table>

    <v-container v-if="!$isLoading('created')" fluid>
      <v-layout row wrap align-center>
        <add-project-modal
          :departments="getDepartments()"
          :projects="projects"
          :categories="getCategories()"
          :responsible="projects.responsible"
          @create="projects.push($event)"
        />
      </v-layout>
    </v-container>

    <remove-project-modal
      v-model="removeProject.modal"
      :project="removeProject.project"
      @remove="projects.splice(projects.indexOf($event), 1)"
    />

    <v-tooltip
      v-if="hoverRemoveButton.target"
      :value="true"
      :activator="hoverRemoveButton.target"
      top
    >
      <span>{{ $language('pages.projects.cantBeDeletedMessage') }}</span>
    </v-tooltip>

    <v-menu
      v-if="dropdown.target"
      v-on-clickaway="dropdownOnClickAway"
      :value="true"
      :close-on-content-click="false"
      :close-on-click="false"
      offset-y
      :close-delay="0"
      :activator="dropdown.target"
      :max-height="300"
    >
      <v-list>
        <v-list-tile
          v-for="(item, index) in dropdownItems"
          :key="index"
          @click="dropdownOnClickItem(item)"
        >
          <v-list-tile-title>
            <v-checkbox
              class="ma-0 pa-0"
              v-if="dropdown.multiple"
              :input-value="dropdown.selected[item[dropdown.itemValue]]"
              :label="item[dropdown.itemText]"
              color="primary"
            />
            <div v-else>{{ item[dropdown.itemText] }}</div>
          </v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-menu>
  </v-container>
</template>

<script>
import { debounce } from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';
import { mapState } from 'vuex';
import tableNavigation from '@/mixin/tableNavigation';
import loading from '@/mixin/loading';
import search from '@/mixin/search';
import departments from '@/mixin/departments';
import categories from '@/mixin/categories';
import statuses from '@/mixin/statuses';
import projects from './projects';
import removeProject from './removeProject';
import dropdown from './dropdown';
import hoverRemoveButton from './hoverRemoveButton';
import AddProjectModal from './AddProjectModal';
import RemoveProjectModal from './RemoveProjectModal';

export default {
  name: 'projects',
  mixins: [clickaway, tableNavigation, loading, search, departments, categories, statuses, projects, removeProject, dropdown, hoverRemoveButton],
  components: { AddProjectModal, RemoveProjectModal },
  data() {
    return {
      months: this.$getMonths(),
      headers: [
        { text: this.$language('pages.projects.table.name'), value: 'Name' },
        { text: this.$language('pages.projects.table.responsible'), value: 'Responsible' },
        { text: this.$language('pages.projects.table.owner'), value: 'DepartmentID' },
        { text: this.$language('pages.projects.table.category'), value: 'ProjectCategoryID' },
        { text: this.$language('pages.projects.table.orderValue'), value: 'OrderValue' },
        { text: this.$language('pages.projects.table.department'), value: 'DepartmentIDs', sortable: false },
        { text: this.$language('pages.projects.table.status'), value: 'ProjectStatusID' },
        { text: this.$language('pages.projects.table.actions'), value: 'actions', sortable: false },
      ],
      pagination: { rowsPerPage: 25 },
      rows: [25, 50, 100],
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    items() {
      const filters = Object.entries({
        DepartmentID: this.getDepartment('ID'),
        ProjectCategoryID: this.getCategory('ID'),
        ProjectStatusID: this.getStatus('ID'),
      }).filter(([, value]) => typeof value !== 'undefined' && value !== null);
      if (filters.length) {
        return this.projects.filter((project) => {
          return filters.every(([field, value]) => {
            return project[field] === value;
          });
        });
      }
      return this.projects;
    },
  },
  methods: {
    searchFilter(items, search) {
      if (!search) {
        return items;
      }
      const searchToLowerCase = search.toLowerCase();
      return items.filter(({ Name, Responsible, OrderValue }) => {
        return (Name && Name.toString().toLowerCase().includes(searchToLowerCase))
          || (Responsible && Responsible.toString().toLowerCase().includes(searchToLowerCase))
          || (OrderValue && OrderValue.toString().toLowerCase().includes(searchToLowerCase));
      });
    },
    async updateDepartmentIDs(input, project, value) {
      value = value.map(number => parseInt(number, 10));

      const oldValue = project.DepartmentIDs;
      const newValue = value;

      const added = newValue.filter(ID => !oldValue.includes(ID));
      const removed = oldValue.filter(ID => !newValue.includes(ID));

      // value hasn't changed
      if (!added.length && !removed.length) {
        return;
      }
      try {
        await this.updateProject(project, 'DepartmentIDs', oldValue, newValue, added, removed);
      } catch (err) {
        console.log(err);
        this.$store.commit('snackbar/show', (err.response && err.response.data && err.response.data.message) || err.message);
      }
    },
    async update(input, project, field, value) {

      if (field === 'DepartmentIDs') {
        value = value.map(number => parseInt(number, 10));
      }

      const oldValue = project[field];
      const newValue = value || '';
      // value hasn't changed
      if (newValue === oldValue) {
        return;
      }
      // set old value if input is empty
      if (value === '') {
        this.$forceUpdate(); // reset input value
        return;
      }
      try {
        await this.updateProject(project, field, oldValue, newValue);
      } catch (err) {
        console.log(err);
        this.$store.commit('snackbar/show', (err.response && err.response.data && err.response.data.message) || err.message);
      }
    },
    updateDebounce: debounce(function updateDebounce(...args) {
      return this.update(...args);
    }, 100),
  },
  async created() {
    document.title = this.$language('pages.projects.title');
    this.$store.commit('page/title', this.$language('pages.projects.menuTitle'));
    this.setAllDepartmentsItem(this.$language('pages.projects.allDepartmentsItem'), this.$language('pages.projects.allDepartmentsItem'));
    this.setAllCategoriesItem(this.$language('pages.projects.allCategoriesItem'));
    this.setAllStatusesItem(this.$language('pages.projects.allStatusesItem'));
    this.$startLoading('created');
    try {
      await Promise.all([
        this.fetchDepartments(),
        this.fetchCategories(),
        this.fetchStatuses(),
        this.fetchProjects(),
      ]);
    } catch (err) {
      console.log(err);
      this.$store.commit('snackbar/show', (err.response && err.response.data && err.response.data.message) || err.message);
      throw err;
    }
    this.$stopLoading('created');
  },
};
</script>

<style lang="scss" scoped>
  .v-menu__content {
    /deep/ .v-list__tile:focus {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }
</style>
